/**
 * Newsletter email builder (email-safe).
 *
 * Genera HTML compatible con Gmail / Outlook / Apple Mail:
 * - Tablas role="presentation" para el layout (no fl-x/grid).
 * - CSS inline en cada elemento + un <style> mínimo con media queries.
 * - Ancho máximo 600px, mobile-first.
 * - Sin el footer de desuscripción: lo agrega la Cloud Function al enviar
 *   (y withPreviewUnsubscribeFooter en la vista previa). Esto evita duplicarlo.
 *
 * Todo es data-driven: una campaña es { design, blocks[] } y buildEmailHtml()
 * produce el HTML final que se guarda en la campaña y se usa para enviar.
 */

let blockSeq = 0;
function genId(prefix = 'blk') {
  blockSeq += 1;
  return `${prefix}_${Date.now().toString(36)}_${blockSeq}`;
}

export const DEFAULT_DESIGN = {
  brandName: 'PRIBALL',
  logoUrl: '',
  primaryColor: '#3b82f6',
  darkMode: false,
  fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif",
  footerTagline: 'De Bell Ville, Capital nacional de la pelota de fútbol.'
};

/** Presets de CTA: cada uno sabe construir el href correcto desde un valor. */
export const CTA_PRESETS = [
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    placeholder: 'Ej: 5491122334455 o un mensaje',
    hint: 'Número con código de país (sin + ni espacios).',
    buildHref: (value) => {
      const digits = String(value || '').replace(/[^\d]/g, '');
      if (!digits) return 'https://wa.me/';
      return `https://wa.me/${digits}`;
    }
  },
  {
    id: 'instagram',
    label: 'Instagram',
    placeholder: 'usuario o URL completa',
    hint: 'Usuario sin @ o URL completa.',
    buildHref: (value) => {
      const raw = String(value || '').trim();
      if (!raw) return 'https://instagram.com/';
      if (/^https?:\/\//i.test(raw)) return raw;
      return `https://instagram.com/${raw.replace(/^@/, '')}`;
    }
  },
  {
    id: 'web',
    label: 'Sitio web',
    placeholder: 'https://www.priball.com',
    hint: 'URL completa con https://',
    buildHref: (value) => normalizeUrl(value, 'https://www.priball.com')
  },
  {
    id: 'mercadopago',
    label: 'MercadoPago',
    placeholder: 'https://mpago.la/...',
    hint: 'Link de pago de MercadoPago.',
    buildHref: (value) => normalizeUrl(value, 'https://www.mercadopago.com.ar')
  },
  {
    id: 'catalogo',
    label: 'Catálogo',
    placeholder: 'https://www.priball.com/productos',
    hint: 'URL del catálogo o categoría.',
    buildHref: (value) => normalizeUrl(value, 'https://www.priball.com/productos')
  }
];

export function getCtaPreset(id) {
  return CTA_PRESETS.find((c) => c.id === id) || CTA_PRESETS[2];
}

export function buildCtaHref(ctaType, ctaUrl) {
  return getCtaPreset(ctaType).buildHref(ctaUrl);
}

function normalizeUrl(value, fallback = '#') {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  if (/^https?:\/\//i.test(raw)) return raw;
  if (/^mailto:|^tel:/i.test(raw)) return raw;
  return `https://${raw}`;
}

/** Catálogo de bloques disponibles (para el selector del constructor). */
export const BLOCK_LIBRARY = [
  { type: 'hero', label: 'Hero', icon: '🖼️', desc: 'Imagen grande + título + oferta + CTA' },
  { type: 'offer', label: 'Oferta', icon: '🏷️', desc: 'Banner de descuento u oferta destacada' },
  { type: 'gif', label: 'GIF / Animación', icon: '🎞️', desc: 'GIF o imagen con botón play falso' },
  { type: 'button', label: 'Botón CTA', icon: '🔘', desc: 'Botón de acción configurable' },
  { type: 'countdown', label: 'Cuenta regresiva', icon: '⏳', desc: 'Tiempo restante hasta una fecha' },
  { type: 'testimonial', label: 'Testimonio', icon: '💬', desc: 'Reseña de un cliente' },
  { type: 'products', label: 'Productos', icon: '🛒', desc: 'Grilla de productos destacados' },
  { type: 'text', label: 'Texto', icon: '📝', desc: 'Párrafo simple con título opcional' }
];

/** Crea un bloque nuevo con valores por defecto razonables. */
export function createBlock(type) {
  const base = { id: genId(type), type };
  switch (type) {
    case 'hero':
      return {
        ...base,
        image: 'https://via.placeholder.com/1200x720?text=Imagen+Hero',
        alt: 'Imagen principal',
        eyebrow: 'NUEVA COLECCIÓN',
        title: 'TU JUEGO,\nTU BALÓN',
        subtitle: 'Rendimiento profesional para cada partido.',
        price: '',
        priceNote: '',
        ctaLabel: 'VER COLECCIÓN',
        ctaType: 'catalogo',
        ctaUrl: 'https://www.priball.com/productos'
      };
    case 'offer':
      return {
        ...base,
        badge: 'OFERTA',
        title: '20% OFF en toda la tienda',
        text: 'Solo por tiempo limitado. Aprovechá antes de que se agote.'
      };
    case 'gif':
      return {
        ...base,
        image: 'https://via.placeholder.com/1200x675?text=GIF+o+Animacion',
        alt: 'Animación del producto',
        fakePlay: true,
        caption: '',
        ctaType: 'web',
        ctaUrl: 'https://www.priball.com'
      };
    case 'button':
      return {
        ...base,
        label: 'COMPRAR AHORA',
        ctaType: 'web',
        ctaUrl: 'https://www.priball.com',
        align: 'center'
      };
    case 'countdown':
      return {
        ...base,
        title: 'La oferta termina en',
        endDate: defaultCountdownDate()
      };
    case 'testimonial':
      return {
        ...base,
        quote: 'La mejor calidad que probé. Los balones aguantan todo el torneo.',
        author: 'Juan P.',
        role: 'Cliente verificado'
      };
    case 'products':
      return {
        ...base,
        title: 'Productos destacados',
        items: []
      };
    case 'text':
      return {
        ...base,
        title: '',
        text: 'Escribí acá tu mensaje.'
      };
    default:
      return base;
  }
}

function defaultCountdownDate() {
  const d = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000);
  // Formato datetime-local YYYY-MM-DDTHH:mm
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/* ------------------------------------------------------------------ */
/* Helpers de escape                                                   */
/* ------------------------------------------------------------------ */

function escHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

function escAttr(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Convierte saltos de línea simples del texto a <br> (para títulos multilinea). */
function nl2br(value) {
  return escHtml(value).replace(/\n/g, '<br>');
}

/* ------------------------------------------------------------------ */
/* Paleta según dark mode                                              */
/* ------------------------------------------------------------------ */

function getPalette(design) {
  const primary = design.primaryColor || DEFAULT_DESIGN.primaryColor;
  if (design.darkMode) {
    return {
      pageBg: '#0a0a0a',
      contentBg: '#111111',
      cardBg: '#1c1c1e',
      text: '#ffffff',
      muted: '#a1a1aa',
      border: '#2a2a2e',
      primary,
      onPrimary: '#ffffff',
      headerBg: '#000000',
      footerBg: '#000000'
    };
  }
  return {
    pageBg: '#f4f4f5',
    contentBg: '#ffffff',
    cardBg: '#fafafa',
    text: '#0a0a0a',
    muted: '#52525b',
    border: '#e4e4e7',
    primary,
    onPrimary: '#ffffff',
    headerBg: '#0a0a0a',
    footerBg: '#fafafa'
  };
}

/* ------------------------------------------------------------------ */
/* Renderers por bloque (devuelven filas <tr> de la tabla de contenido)*/
/* ------------------------------------------------------------------ */

function renderButtonAnchor(label, href, p, font, { full = false } = {}) {
  return `<a href="${escAttr(href)}" target="_blank" rel="noopener noreferrer" style="display:inline-block;${full ? 'width:auto;' : ''}background-color:${p.primary};color:${p.onPrimary};font-family:${font};font-size:16px;font-weight:700;line-height:1;text-decoration:none;text-transform:uppercase;letter-spacing:0.5px;padding:16px 34px;border-radius:8px;mso-padding-alt:16px 34px;">${escHtml(label)}</a>`;
}

function renderHero(b, p, font) {
  const href = buildCtaHref(b.ctaType, b.ctaUrl);
  const priceRow = b.price
    ? `<tr><td style="padding:8px 0 0;font-family:${font};color:${p.primary};font-size:30px;font-weight:800;">${escHtml(b.price)}${b.priceNote ? ` <span style="color:${p.muted};font-size:14px;font-weight:500;">${escHtml(b.priceNote)}</span>` : ''}</td></tr>`
    : '';
  const eyebrowRow = b.eyebrow
    ? `<tr><td style="padding:0 0 10px;font-family:${font};color:${p.primary};font-size:13px;font-weight:700;letter-spacing:2px;text-transform:uppercase;">${escHtml(b.eyebrow)}</td></tr>`
    : '';
  const subtitleRow = b.subtitle
    ? `<tr><td style="padding:12px 0 0;font-family:${font};color:${p.muted};font-size:16px;line-height:1.6;">${nl2br(b.subtitle)}</td></tr>`
    : '';
  const ctaRow = b.ctaLabel
    ? `<tr><td style="padding:24px 0 0;">${renderButtonAnchor(b.ctaLabel, href, p, font)}</td></tr>`
    : '';
  const imageRow = b.image
    ? `<tr><td style="padding:0;"><img src="${escAttr(b.image)}" alt="${escAttr(b.alt)}" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;outline:none;text-decoration:none;" /></td></tr>`
    : '';
  return `<tr><td style="padding:0;background-color:${p.contentBg};">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
      ${imageRow}
      <tr><td style="padding:32px 36px 40px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
          ${eyebrowRow}
          <tr><td style="font-family:${font};color:${p.text};font-size:34px;line-height:1.1;font-weight:800;letter-spacing:-0.5px;">${nl2br(b.title)}</td></tr>
          ${subtitleRow}
          ${priceRow}
          ${ctaRow}
        </table>
      </td></tr>
    </table>
  </td></tr>`;
}

function renderOffer(b, p, font) {
  return `<tr><td style="padding:14px 36px;background-color:${p.contentBg};">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${p.primary};border-radius:12px;">
      <tr><td style="padding:26px 28px;text-align:center;">
        ${b.badge ? `<div style="display:inline-block;background-color:rgba(255,255,255,0.18);color:${p.onPrimary};font-family:${font};font-size:12px;font-weight:700;letter-spacing:2px;text-transform:uppercase;padding:6px 14px;border-radius:999px;margin-bottom:10px;">${escHtml(b.badge)}</div>` : ''}
        <div style="font-family:${font};color:${p.onPrimary};font-size:26px;font-weight:800;line-height:1.2;">${nl2br(b.title)}</div>
        ${b.text ? `<div style="font-family:${font};color:rgba(255,255,255,0.9);font-size:15px;line-height:1.6;margin-top:8px;">${nl2br(b.text)}</div>` : ''}
      </td></tr>
    </table>
  </td></tr>`;
}

function renderGif(b, p, font) {
  const href = buildCtaHref(b.ctaType, b.ctaUrl);
  // "Play falso": overlay con un círculo y triángulo, dibujado con texto/HTML simple.
  const playOverlay = b.fakePlay
    ? `<div style="text-align:center;margin-top:-64px;margin-bottom:24px;position:relative;">
         <span style="display:inline-block;width:64px;height:64px;line-height:64px;border-radius:999px;background-color:rgba(0,0,0,0.55);color:#ffffff;font-size:24px;text-align:center;">&#9654;</span>
       </div>`
    : '';
  const img = `<img src="${escAttr(b.image)}" alt="${escAttr(b.alt)}" width="528" style="display:block;width:100%;max-width:528px;height:auto;border:0;border-radius:12px;outline:none;text-decoration:none;" />`;
  const inner = href && href !== '#'
    ? `<a href="${escAttr(href)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">${img}</a>`
    : img;
  return `<tr><td style="padding:18px 36px;background-color:${p.contentBg};text-align:center;">
    ${inner}
    ${playOverlay}
    ${b.caption ? `<div style="font-family:${font};color:${p.muted};font-size:13px;margin-top:10px;">${escHtml(b.caption)}</div>` : ''}
  </td></tr>`;
}

function renderButton(b, p, font) {
  const href = buildCtaHref(b.ctaType, b.ctaUrl);
  const align = ['left', 'center', 'right'].includes(b.align) ? b.align : 'center';
  return `<tr><td style="padding:10px 36px 22px;background-color:${p.contentBg};text-align:${align};">
    ${renderButtonAnchor(b.label, href, p, font)}
  </td></tr>`;
}

function renderCountdown(b, p, font) {
  const target = b.endDate ? new Date(b.endDate) : null;
  let days = 0;
  let hours = 0;
  let minutes = 0;
  let expired = false;
  if (target && !Number.isNaN(target.getTime())) {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) {
      expired = true;
    } else {
      days = Math.floor(diff / 86400000);
      hours = Math.floor((diff % 86400000) / 3600000);
      minutes = Math.floor((diff % 3600000) / 60000);
    }
  }
  const cell = (value, label) => `<td align="center" style="padding:0 6px;">
      <div style="background-color:${p.cardBg};border:1px solid ${p.border};border-radius:10px;padding:14px 0;min-width:70px;">
        <div style="font-family:${font};color:${p.text};font-size:30px;font-weight:800;line-height:1;">${String(value).padStart(2, '0')}</div>
        <div style="font-family:${font};color:${p.muted};font-size:11px;letter-spacing:1px;text-transform:uppercase;margin-top:6px;">${label}</div>
      </div>
    </td>`;
  const body = expired
    ? `<div style="font-family:${font};color:${p.muted};font-size:16px;">La oferta finalizó.</div>`
    : `<table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center"><tr>${cell(days, 'Días')}${cell(hours, 'Horas')}${cell(minutes, 'Min')}</tr></table>`;
  return `<tr><td style="padding:22px 36px;background-color:${p.contentBg};text-align:center;">
    ${b.title ? `<div style="font-family:${font};color:${p.text};font-size:18px;font-weight:700;margin-bottom:16px;">${escHtml(b.title)}</div>` : ''}
    ${body}
  </td></tr>`;
}

function renderTestimonial(b, p, font) {
  return `<tr><td style="padding:18px 36px;background-color:${p.contentBg};">
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${p.cardBg};border:1px solid ${p.border};border-radius:12px;">
      <tr><td style="padding:26px 28px;text-align:center;">
        <div style="font-family:${font};color:${p.primary};font-size:34px;line-height:1;">&#10077;</div>
        <div style="font-family:${font};color:${p.text};font-size:17px;line-height:1.6;font-style:italic;margin-top:6px;">${nl2br(b.quote)}</div>
        <div style="font-family:${font};color:${p.text};font-size:14px;font-weight:700;margin-top:14px;">${escHtml(b.author)}</div>
        ${b.role ? `<div style="font-family:${font};color:${p.muted};font-size:12px;margin-top:2px;">${escHtml(b.role)}</div>` : ''}
      </td></tr>
    </table>
  </td></tr>`;
}

function renderProducts(b, p, font) {
  const items = Array.isArray(b.items) ? b.items : [];
  if (items.length === 0) {
    return `<tr><td style="padding:18px 36px;background-color:${p.contentBg};">
      ${b.title ? `<div style="font-family:${font};color:${p.text};font-size:20px;font-weight:800;margin-bottom:8px;">${escHtml(b.title)}</div>` : ''}
      <div style="font-family:${font};color:${p.muted};font-size:14px;">Agregá productos a este bloque.</div>
    </td></tr>`;
  }
  const cells = items.map((it) => {
    const href = normalizeUrl(it.url, 'https://www.priball.com/productos');
    const img = it.image
      ? `<img src="${escAttr(it.image)}" alt="${escAttr(it.name || 'Producto')}" width="240" style="display:block;width:100%;max-width:240px;height:auto;border:0;border-radius:10px 10px 0 0;outline:none;text-decoration:none;" />`
      : '';
    const price = it.price
      ? `<div style="font-family:${font};color:${p.primary};font-size:16px;font-weight:800;margin-top:4px;">${escHtml(it.price)}</div>`
      : '';
    return `<td width="50%" valign="top" class="np-col" style="padding:8px;">
        <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${p.cardBg};border:1px solid ${p.border};border-radius:12px;">
          <tr><td style="padding:0;"><a href="${escAttr(href)}" target="_blank" rel="noopener noreferrer" style="text-decoration:none;">${img}</a></td></tr>
          <tr><td style="padding:12px 14px 16px;">
            <div style="font-family:${font};color:${p.text};font-size:14px;font-weight:600;line-height:1.4;">${escHtml(it.name || 'Producto')}</div>
            ${price}
            <div style="margin-top:10px;"><a href="${escAttr(href)}" target="_blank" rel="noopener noreferrer" style="font-family:${font};color:${p.primary};font-size:13px;font-weight:700;text-decoration:none;">Ver producto &#8594;</a></div>
          </td></tr>
        </table>
      </td>`;
  });

  // Agrupar de a 2 columnas por fila
  const rows = [];
  for (let i = 0; i < cells.length; i += 2) {
    const pair = cells.slice(i, i + 2).join('');
    const filler = cells.slice(i, i + 2).length === 1 ? '<td width="50%" style="padding:8px;"></td>' : '';
    rows.push(`<tr>${pair}${filler}</tr>`);
  }

  return `<tr><td style="padding:18px 28px;background-color:${p.contentBg};">
    ${b.title ? `<div style="font-family:${font};color:${p.text};font-size:20px;font-weight:800;margin:0 8px 8px;">${escHtml(b.title)}</div>` : ''}
    <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">${rows.join('')}</table>
  </td></tr>`;
}

function renderText(b, p, font) {
  return `<tr><td style="padding:14px 36px;background-color:${p.contentBg};">
    ${b.title ? `<div style="font-family:${font};color:${p.text};font-size:20px;font-weight:800;margin-bottom:8px;">${escHtml(b.title)}</div>` : ''}
    <div style="font-family:${font};color:${p.muted};font-size:15px;line-height:1.7;">${nl2br(b.text)}</div>
  </td></tr>`;
}

function renderBlock(block, p, font) {
  switch (block.type) {
    case 'hero':
      return renderHero(block, p, font);
    case 'offer':
      return renderOffer(block, p, font);
    case 'gif':
      return renderGif(block, p, font);
    case 'button':
      return renderButton(block, p, font);
    case 'countdown':
      return renderCountdown(block, p, font);
    case 'testimonial':
      return renderTestimonial(block, p, font);
    case 'products':
      return renderProducts(block, p, font);
    case 'text':
      return renderText(block, p, font);
    default:
      return '';
  }
}

/* ------------------------------------------------------------------ */
/* Documento completo                                                  */
/* ------------------------------------------------------------------ */

export function buildEmailHtml(blocks, designInput) {
  const design = { ...DEFAULT_DESIGN, ...(designInput || {}) };
  const p = getPalette(design);
  const font = design.fontFamily || DEFAULT_DESIGN.fontFamily;
  const list = Array.isArray(blocks) ? blocks : [];

  const header = `<tr><td style="padding:22px 36px;background-color:${p.headerBg};text-align:center;">
    ${design.logoUrl
      ? `<img src="${escAttr(design.logoUrl)}" alt="${escAttr(design.brandName)}" height="34" style="display:inline-block;height:34px;width:auto;border:0;outline:none;text-decoration:none;" />`
      : `<span style="font-family:${font};color:#ffffff;font-size:22px;font-weight:800;letter-spacing:3px;text-transform:uppercase;">${escHtml(design.brandName)}</span>`}
  </td></tr>`;

  const footer = `<tr><td style="padding:28px 36px;background-color:${p.footerBg};text-align:center;border-top:1px solid ${p.border};">
    <div style="font-family:${font};color:${p.muted};font-size:13px;line-height:1.6;font-weight:600;">Priball · Balones deportivos</div>
    <div style="font-family:${font};color:${p.muted};font-size:12px;line-height:1.6;margin-top:4px;">De Bell Ville, Capital nacional de la pelota de fútbol.</div>
    <div style="font-family:${font};font-size:12px;line-height:1.6;margin-top:6px;"><a href="https://www.priball.com" style="color:${p.muted};text-decoration:underline;">www.priball.com</a></div>
  </td></tr>`;

  const body = list.map((b) => renderBlock(b, p, font)).join('\n');

  return `<!DOCTYPE html>
<html lang="es" xmlns="http://www.w3.org/1999/xhtml">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<meta http-equiv="X-UA-Compatible" content="IE=edge" />
<meta name="color-scheme" content="${design.darkMode ? 'dark' : 'light'}" />
<meta name="supported-color-schemes" content="light dark" />
<title>${escHtml(design.brandName)}</title>
<style>
  body { margin:0; padding:0; width:100% !important; -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%; }
  table { border-collapse:collapse !important; }
  img { -ms-interpolation-mode:bicubic; }
  a { text-decoration:none; }
  @media only screen and (max-width:600px) {
    .np-container { width:100% !important; }
    .np-col { display:block !important; width:100% !important; }
    .np-px { padding-left:20px !important; padding-right:20px !important; }
  }
</style>
</head>
<body style="margin:0;padding:0;background-color:${p.pageBg};">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:${p.pageBg};">
    <tr>
      <td align="center" style="padding:24px 12px;">
        <table role="presentation" class="np-container" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background-color:${p.contentBg};border-radius:14px;overflow:hidden;">
          ${header}
          ${body}
          ${footer}
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}

/* ------------------------------------------------------------------ */
/* Plantillas premium (Nike / Adidas / Apple)                          */
/* ------------------------------------------------------------------ */

export const EMAIL_TEMPLATES = [
  {
    id: 'nike',
    name: 'Nike',
    desc: 'Oscuro, deportivo, CTA fuerte.',
    build: () => ({
      design: {
        ...DEFAULT_DESIGN,
        brandName: 'PRIBALL',
        primaryColor: '#e11d48',
        darkMode: true,
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
      },
      blocks: [
        {
          ...createBlock('hero'),
          eyebrow: 'JUST PLAY',
          title: 'DOMINÁ\nLA CANCHA',
          subtitle: 'Balones de competición diseñados para ganar.',
          price: '',
          ctaLabel: 'COMPRAR AHORA',
          ctaType: 'catalogo',
          ctaUrl: 'https://www.priball.com/productos',
          image: 'https://via.placeholder.com/1200x800/0a0a0a/ffffff?text=HERO'
        },
        { ...createBlock('offer'), badge: 'DROP', title: 'Hasta 30% OFF', text: 'Edición limitada. Mientras dure el stock.' },
        { ...createBlock('button'), label: 'VER COLECCIÓN', ctaType: 'catalogo', ctaUrl: 'https://www.priball.com/productos' }
      ]
    })
  },
  {
    id: 'adidas',
    name: 'Adidas',
    desc: 'Limpio, alto contraste, foco en producto.',
    build: () => ({
      design: {
        ...DEFAULT_DESIGN,
        brandName: 'PRIBALL',
        primaryColor: '#111111',
        darkMode: false,
        fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif"
      },
      blocks: [
        {
          ...createBlock('hero'),
          eyebrow: 'IMPOSSIBLE IS NOTHING',
          title: 'NUEVA\nTEMPORADA',
          subtitle: 'Tecnología y diseño en cada balón.',
          ctaLabel: 'EXPLORAR',
          ctaType: 'catalogo',
          ctaUrl: 'https://www.priball.com/productos',
          image: 'https://via.placeholder.com/1200x800/ffffff/111111?text=HERO'
        },
        { ...createBlock('products'), title: 'Lo más vendido', items: [] },
        { ...createBlock('countdown'), title: 'La promo termina en' }
      ]
    })
  },
  {
    id: 'apple',
    name: 'Apple',
    desc: 'Minimalista, mucho espacio, elegante.',
    build: () => ({
      design: {
        ...DEFAULT_DESIGN,
        brandName: 'PRIBALL',
        primaryColor: '#2563eb',
        darkMode: true,
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif"
      },
      blocks: [
        {
          ...createBlock('hero'),
          eyebrow: '',
          title: 'Priball Pro',
          subtitle: 'Precisión. Control. Potencia.',
          ctaLabel: 'Conocé más',
          ctaType: 'web',
          ctaUrl: 'https://www.priball.com',
          image: 'https://via.placeholder.com/1200x800/000000/ffffff?text=PRO'
        },
        { ...createBlock('text'), title: '', text: 'Diseñado para quienes no se conforman. Cada detalle, pensado para el máximo rendimiento.' },
        { ...createBlock('button'), label: 'Comprar', ctaType: 'web', ctaUrl: 'https://www.priball.com' }
      ]
    })
  }
];

export function getTemplate(id) {
  return EMAIL_TEMPLATES.find((t) => t.id === id) || null;
}
